import React from "react";
import { Baby, Stethoscope, AlertTriangle, MapPin, Clock, FileText } from "lucide-react";
import { InputField, TextAreaField, RadioField } from "../form/FormComponents_PediatricsRegistration";
import type { FormData } from "../../types/consultation";

interface PrenatalBirthSectionProps {
  formData: FormData;
  updatePediatricsInfo: (field: string, value: string | boolean) => void;
}

export const PrenatalBirthSection: React.FC<PrenatalBirthSectionProps> = ({
  formData,
  updatePediatricsInfo,
}) => {
  return (
    <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-6 rounded-2xl shadow-lg border border-purple-100 mb-8">
      <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
        <Baby className="mr-2 text-purple-600" size={24} />
        Histoire Prénatale / Naissance
      </h3>

      <div className="space-y-6">
        <TextAreaField
          label="Où la mère a-t-elle reçu des soins prénataux"
          value={formData.pediatrieInfo?.soinsPrenataux || ""}
          onChange={(value) => updatePediatricsInfo("soinsPrenataux", value)}
          icon={Stethoscope}
          placeholder="Lieu des soins prénataux..."
        />

        <TextAreaField
          label="Y a-t-il eu des complications pendant la grossesse"
          value={formData.pediatrieInfo?.complicationsGrossesse || ""}
          onChange={(value) =>
            updatePediatricsInfo("complicationsGrossesse", value)
          }
          icon={AlertTriangle}
          placeholder="Décrivez les complications s'il y en a eu..."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField
            label="Où est né l'enfant"
            value={formData.pediatrieInfo?.lieuNaissance || ""}
            onChange={(value) => updatePediatricsInfo("lieuNaissance", value)}
            icon={MapPin}
            placeholder="Lieu de naissance"
          />

          <RadioField
            label="Type d'accouchement"
            options={[
              { value: "normale", label: "Normale" },
              { value: "cesarienne", label: "Césarienne" },
              { value: "forceps", label: "Forceps" },
            ]}
            selectedValue={formData.pediatrieInfo?.typeAccouchement || ""}
            onChange={(value) =>
              updatePediatricsInfo("typeAccouchement", value)
            }
            icon={Baby}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField
            label="Poids du bébé à la naissance (kg)"
            value={formData.pediatrieInfo?.poidsNaissance || ""}
            onChange={(value) =>
              updatePediatricsInfo("poidsNaissance", value)
            }
            type="number"
            step="0.1"
            icon={Baby}
            placeholder="Ex: 3.2"
          />

          <InputField
            label="Âge gestationnel"
            value={formData.pediatrieInfo?.ageGestationnel || ""}
            onChange={(value) =>
              updatePediatricsInfo("ageGestationnel", value)
            }
            icon={Clock}
            placeholder="Ex: 39 semaines"
          />
        </div>

        {/* Complications pendant ou après l'accouchement avec Oui/Non + explication */}
        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-gray-700">
            <AlertTriangle className="mr-2 text-purple-500" size={18} />
            Y a-t-il eu des complications pendant ou après l'accouchement ?
          </label>
          <div className="flex items-center gap-6 mt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="complicationsAccouchement"
                checked={formData.pediatrieInfo?.complicationsAccouchement === "oui"}
                onChange={() => updatePediatricsInfo("complicationsAccouchement", "oui")}
                className="accent-purple-600"
              />
              <span className="text-sm">Oui</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="complicationsAccouchement"
                checked={formData.pediatrieInfo?.complicationsAccouchement === "non"}
                onChange={() => updatePediatricsInfo("complicationsAccouchement", "non")}
                className="accent-purple-600"
              />
              <span className="text-sm">Non</span>
            </label>
          </div>
          {formData.pediatrieInfo?.complicationsAccouchement === "oui" && (
            <TextAreaField
              label="Veuillez décrire les complications"
              value={formData.pediatrieInfo?.descriptionComplications || ""}
              onChange={(value) =>
                updatePediatricsInfo("descriptionComplications", value)
              }
              icon={FileText}
              placeholder="Description détaillée des complications..."
              rows={4}
            />
          )}
        </div>
      </div>
    </div>
  );
};